import {
  AUDIOLOGIST_PRESCRIPTION_TYPE_OPTIONS,
  AUDIOLOGIST_LENS_TYPE_OPTIONS,
  AUDIOLOGIST_INDEX_OPTIONS,
  AUDIOLOGIST_MOUNTING_OPTIONS,
} from "../mocks/va/mockData";
import {
  getLensSelectionEntry,
  normalizeLensItems,
} from "./prescriptionFormat";

const EMPTY_LENS_SELECTION = {
  prescriptionType: "",
  lensType: "",
  indexType: "",
  mountingType: "",
};

const PRESCRIPTION_RULES = [
  { pattern: /progressive/i, value: "Progressive Lenses" },
  { pattern: /single\s*vision/i, value: "Single vision" },
  { pattern: /reading/i, value: "Reading Lenses" },
  { pattern: /bifocal/i, value: "Bifocal Lenses" },
  {
    pattern: /don'?t need|no prescription/i,
    value: "I Don't Need Prescription Lenses",
  },
];

const LENS_TYPE_RULES = [
  { pattern: /\bblf\b|\bblp\b|blue\s*light/i, value: "Blue Light Filtering Lenses" },
  { pattern: /\bclear\b|\bclr\b/i, value: "Clear Lenses" },
];

const INDEX_RULES = [
  { pattern: /\b1\.74\b/, value: "1.74" },
  { pattern: /\b1\.67\b/, value: "1.67" },
  { pattern: /\b1\.60\b/, value: "1.60" },
];

const MOUNTING_RULES = [
  { pattern: /bonded/i, value: "bonded" },
  { pattern: /insert/i, value: "inserts" },
];

function isValidOptionValue(options, value) {
  return Boolean(
    value && options.some((option) => option.value === value)
  );
}

function matchByRules(rules, options, haystack) {
  for (const rule of rules) {
    if (!rule.pattern.test(haystack)) continue;
    if (isValidOptionValue(options, rule.value)) {
      return rule.value;
    }
  }
  return "";
}

function matchOptionInText(options, haystack) {
  for (const option of options) {
    const value = String(option.value || "").trim();
    const label = String(option.label || "").trim();
    if (!value && !label) continue;

    const valuePattern = value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const labelPattern = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    if (
      (valuePattern && new RegExp(valuePattern, "i").test(haystack)) ||
      (labelPattern && new RegExp(labelPattern, "i").test(haystack))
    ) {
      return option.value;
    }
  }
  return "";
}

/**
 * Parse Shopify bundle lens label into audiologist lens dropdown values.
 * Examples:
 * - "Myvu - Progressive - BLF - 1.74"
 * - "BLF - Progressive - 1.74 - Bonded"
 * - "Myvu - Progressive - Clear - 1.60"
 */
export function parseBundleLensLabel(label = "") {
  const haystack = String(label || "").trim();
  if (!haystack) {
    return { ...EMPTY_LENS_SELECTION };
  }

  const prescriptionType =
    matchByRules(
      PRESCRIPTION_RULES,
      AUDIOLOGIST_PRESCRIPTION_TYPE_OPTIONS,
      haystack
    ) ||
    matchOptionInText(AUDIOLOGIST_PRESCRIPTION_TYPE_OPTIONS, haystack);

  const lensType =
    matchByRules(LENS_TYPE_RULES, AUDIOLOGIST_LENS_TYPE_OPTIONS, haystack) ||
    matchOptionInText(AUDIOLOGIST_LENS_TYPE_OPTIONS, haystack);

  const indexType =
    matchByRules(INDEX_RULES, AUDIOLOGIST_INDEX_OPTIONS, haystack) ||
    matchOptionInText(AUDIOLOGIST_INDEX_OPTIONS, haystack);

  const mountingType =
    matchByRules(MOUNTING_RULES, AUDIOLOGIST_MOUNTING_OPTIONS, haystack) ||
    matchOptionInText(AUDIOLOGIST_MOUNTING_OPTIONS, haystack);

  return {
    prescriptionType,
    lensType,
    indexType,
    mountingType,
  };
}

export function shortenLensLabel(label = "") {
  const parsed = parseBundleLensLabel(label);
  const parts = [];

  if (parsed.lensType) {
    const lensOption = AUDIOLOGIST_LENS_TYPE_OPTIONS.find(
      (option) => option.value === parsed.lensType
    );
    parts.push(lensOption?.label || parsed.lensType);
  }

  if (parsed.indexType) {
    const indexOption = AUDIOLOGIST_INDEX_OPTIONS.find(
      (option) => option.value === parsed.indexType
    );
    parts.push(indexOption?.label || parsed.indexType);
  }

  if (parts.length > 0) {
    return parts.join(" · ");
  }

  const tokens = String(label || "")
    .split(/\s*-\s*/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (tokens.length >= 2) {
    return tokens.slice(-2).join(" · ");
  }

  return String(label || "").trim();
}

export function mergeLensSelection(saved = {}, fromBundle = {}) {
  return {
    prescriptionType:
      saved.prescriptionType || fromBundle.prescriptionType || "",
    lensType: saved.lensType || fromBundle.lensType || "",
    indexType: saved.indexType || fromBundle.indexType || "",
    mountingType: saved.mountingType || fromBundle.mountingType || "",
  };
}

export function createLensSelectionsFromBundle({
  lensItems = [],
  lensCount = 1,
  initialSelection = null,
}) {
  const labels = normalizeLensItems(lensItems);

  return Array.from({ length: lensCount }, (_, index) => {
    const saved = getLensSelectionEntry(initialSelection, index) || {};
    const fromBundle = parseBundleLensLabel(
      labels[index] || labels[0] || ""
    );

    return mergeLensSelection(saved, fromBundle);
  });
}
